import { WILL_YOU_LIST_ALL_PRODUCTS_ID } from "@faire/help-center-utils/articleIds";
import { formatMoney } from "@faire/web--source/common/money";
import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import supportArticle from "@faire/web-api--source/routes/support/articles/articleId";
import termsOfService from "@faire/web-api--source/routes/tos";
import React, { JSX } from "react";

import { RETAIL_PRICE_THRESHOLD } from "../../utils/productPricing";
import { LiItem } from "../common/LiItem";

export const AllowedAndRestrictedProductsIA = () => {
  return (
    <Column>
      <Typography>
        <StrictLocalMsg
          defaultMessage="Any products available to purchase wholesale on other channels should be listed on Faire at the same or lower price point. This includes:"
          description={{
            text: "Introduction to allowed products",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="ATS (available to ship) products, and all product variants;"
              description={{
                text: "First allowed product type",
              }}
            />
          </Typography>
        </LiItem>
        <LiItem step={2}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Pre-order products and seasonal products;"
              description={{
                text: "Second allowed product type",
              }}
            />
          </Typography>
        </LiItem>
        <LiItem step={3}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Made-to-order products;"
              description={{
                text: "Third allowed product type",
              }}
            />
          </Typography>
        </LiItem>
        <LiItem step={4}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Discounted products."
              description={{
                text: "Fourth allowed product type",
              }}
            />
          </Typography>
        </LiItem>
      </ol>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="While we encourage you to list your full wholesale selection on Faire, we understand that not all products can be listed. Excluded products may include:"
          description={{
            text: "Introduction to excluded products",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Items that are sold direct to consumer only (not intended for wholesale);"
              description={{
                text: "First excluded product type",
              }}
            />
          </Typography>
        </LiItem>
        <LiItem step={2}>
          <Typography>
            {/* Use hook for formatMoney once instant answers are migrated to hooks */}
            <StrictLocalMsg
              defaultMessage="Items with a retail price over {retailerPriceThresholdMoney}, as they don’t typically perform well on our platform;"
              values={{
                retailerPriceThresholdMoney: formatMoney({
                  money: RETAIL_PRICE_THRESHOLD,
                }),
              }}
              description={{
                text: "Second excluded product type",
              }}
            />
          </Typography>
        </LiItem>
        <LiItem step={3}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Items that have limited inventory, are soon to be out of stock or discontinued, or are not currently available for purchase."
              description={{
                text: "Third excluded product type",
              }}
            />
          </Typography>
        </LiItem>
      </ol>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="Additionally, some products are not allowed on Faire. For a full list, please see our Help Center article, <ArticleLinkFormatting>Will you list all of my products</ArticleLinkFormatting>, or refer to our <TermsOfServiceLinkFormatting>Terms of Service</TermsOfServiceLinkFormatting>."
          values={{
            ArticleLinkFormatting,
            TermsOfServiceLinkFormatting,
          }}
          description={{
            text: "Conclusion about restricted products",
          }}
        />
      </Typography>
    </Column>
  );
};

const ArticleLinkFormatting = (chunks: JSX.Element) => (
  <Button
    variant="tertiary"
    linkTo={supportArticle(WILL_YOU_LIST_ALL_PRODUCTS_ID)}
    target="_blank"
  >
    {chunks}
  </Button>
);

const TermsOfServiceLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={termsOfService()} target="_blank">
    {chunks}
  </Button>
);
